/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { ApolloError } from "@apollo/client";
import {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from "next";
import { getBrand } from "services/algolia/get-brand";
import { getNavigationItems } from "services/graphql/get-navigation-items";
import { getProductBySlug } from "services/graphql/get-product-by-slug";
import { PromotionContextProvider } from "src/contexts/promotion.context";
import { ProductView } from "src/organisms/ProductView";
import { AlgoliaSearchBrand } from "src/types/Algolia";
import { NavigationItems, ShopType, SingleProduct } from "src/types/graphql.d";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { useProductSchema } from "src/hooks/useProductSchema";

type ProductPageProps = {
  product?: SingleProduct;
  brand?: AlgoliaSearchBrand | null;
  error: ApolloError | string | null;
  loading: boolean;
  slug: string;
  gender: string | null;
  navigationItems?: NavigationItems | null;
  clientNavigation: boolean;
};

const ProductPage = ({
  pageProps: { product, error, loading = true, slug, brand },
}: {
  pageProps: ProductPageProps;
}): JSX.Element => {
  // Generate schema for the product
  useProductSchema(product, slug);

  return (
    <PromotionContextProvider>
      <ProductView
        product={product}
        productError={error}
        productLoading={loading}
        slug={slug}
        brand={brand}
      />
    </PromotionContextProvider>
  );
};

export function getStaticPaths(): GetStaticPathsResult {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export async function getStaticProps({
  params,
}: GetStaticPropsContext<{
  productSlug: string;
}>): Promise<GetStaticPropsResult<ProductPageProps>> {
  if (!params || !params.productSlug) {
    return {
      notFound: true,
    };
  }

  const slug = params.productSlug;
  const product = await getProductBySlug(slug);

  if (!product) {
    return { notFound: true };
  }

  const brand = (await getBrand(product.brand.slug)) || null;
  const isMultipleShopType = (product.product_shop_type?.length || 0) > 1;
  const gender = isMultipleShopType
    ? null
    : product.gender?.toLowerCase() || null;

  const { navigationItems } =
    (await getNavigationItems(
      localeFix({
        gender: gender || "women",
      }) as ShopType
    )) || [];

  return {
    revalidate: 60 * 15,
    props: {
      product,
      brand,
      loading: false,
      error: null,
      slug,
      clientNavigation: isMultipleShopType, // Temporary fix for cart because of genderless pages
      navigationItems: isMultipleShopType ? null : navigationItems, // If it's a unisex load navigation from client side
      gender,
    },
  };
}

export default ProductPage;
